// Settings > Onboarding > Shopify custom pixel. gt.js can't run on Shopify's
// checkout, so Shopify brands also install a custom pixel that reports
// product views, add-to-carts, checkout starts and purchases. Free, standard
// and enterprise brands only — the API answers available: false for
// network plans and Onboarding leaves this section out.
import React, { useState } from 'react'
import { Button } from '@mui/material'
import toast from 'react-hot-toast'
import axiosInstant from '../../../../api'
import { brandShopifyPixelEndpoint } from '../../../../api/endpoints'
import { Snippet } from '../GoogleTag'
import { shopifyPixelSnippet } from './shopifyPixelSnippet'
import ReportStatus from './ReportStatus'

export interface ShopifyPixelState {
  available: boolean
  public_token: string | null
  last_event_at: string | null
  // From the newest evidence: a pixel event, or the last install check.
  installed?: boolean
  created_at: string | null
  hosts: string[]
}

export default function ShopifyPixel({
  brandId,
  pixel,
  onChange,
}: {
  brandId: number
  pixel: ShopifyPixelState
  onChange: (next: ShopifyPixelState) => void
}) {
  const [busy, setBusy] = useState(false)

  const generate = async () => {
    setBusy(true)
    try {
      const { data } = await axiosInstant.post(brandShopifyPixelEndpoint(brandId))
      onChange(data)
      toast.success('Pixel generated — copy it into Shopify below.')
    } catch {
      toast.error("Couldn't generate the pixel. Please try again.")
    } finally {
      setBusy(false)
    }
  }

  const disconnect = async () => {
    if (
      !window.confirm(
        'Disconnect the Shopify pixel? Events from the pixel already installed in Shopify will stop being recorded, and a new pixel will have a new store id.'
      )
    )
      return
    setBusy(true)
    try {
      const { data } = await axiosInstant.delete(brandShopifyPixelEndpoint(brandId))
      onChange(data)
      toast.success('Shopify pixel disconnected.')
    } catch {
      toast.error("Couldn't disconnect the pixel.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-text-2 max-w-[720px]">
        Shopify’s checkout doesn’t run site scripts, so on a Shopify store the tag above can’t see checkouts or
        orders. Add this custom pixel to report product views, add-to-carts, checkout starts and purchases. Once
        it reports, it takes over cart, checkout and order tracking from the tag so nothing is counted twice.
      </p>

      {!pixel.public_token ? (
        <div>
          <Button variant="contained" disableElevation onClick={generate} disabled={busy}>
            {busy ? 'Generating…' : 'Generate pixel'}
          </Button>
        </div>
      ) : (
        <>
          <ReportStatus
            installed={!!pixel.installed}
            lastAt={pixel.last_event_at}
            activeLabel="Pixel installed"
            idleLabel={pixel.last_event_at ? 'Not detected' : 'Waiting for first event'}
          />

          <ol className="list-decimal pl-5 text-[13px] text-text-2 flex flex-col gap-1">
            <li>
              In Shopify Admin, open <strong>Settings → Customer events</strong>.
            </li>
            <li>
              Click <strong>Add custom pixel</strong> and name it (for example “TDC”).
            </li>
            <li>Delete the placeholder code, paste the code below, and click Save.</li>
            <li>
              Click <strong>Connect</strong>, then use <strong>Check installation</strong> at the top of this page.
            </li>
          </ol>

          <Snippet label="Pixel code" code={shopifyPixelSnippet(pixel.public_token)} />

          <p className="text-[12px] text-text-2 max-w-[720px]">
            Events are accepted from any <code>*.myshopify.com</code> store
            {pixel.hosts.length ? (
              <>
                {' '}
                and from <strong>{pixel.hosts.join(', ')}</strong>
              </>
            ) : null}
            . If your storefront uses another domain, add it under White Label (or ask a brand admin to).
          </p>

          <div>
            <Button size="small" variant="text" color="inherit" onClick={disconnect} disabled={busy}>
              Disconnect pixel
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
